// PipelineScores.jsx — Real-time pipeline score leaderboard (Jun 29 2026)
// Reads from /api/pipeline-scores → Redis keys pipeline:score:{symbol} (TTL 90s)
// No DB persistence — purely ephemeral live view of every pipeline run.
// v2: fixed bar width (w-28), IST timestamp, column headers.
import React, { useState, useMemo } from "react";

const ALERT_THRESHOLD_STOCK = 8.5;
const ALERT_THRESHOLD_INDEX = 7.5;
const RAW_LOG_MIN_STOCK     = 7.5;
const RAW_LOG_MIN_INDEX     = 6.5;

const TRIGGER_LABELS = {
  timer:                "Timer",
  timer_v2:             "Timer v2",
  event:                "Event",
  sr_touch:             "SR Touch",
  sr_breakout:          "SR Brkout",
  swing_breakout:       "Swing BO",
  orb:                  "ORB",
  compression_breakout: "Compr",
  intraday_scalp:       "Scalp",
  tick:                 "Tick",
  news:                 "News",
};

const TRIGGER_COLORS = {
  timer:                "bg-blue-100 text-blue-700",
  timer_v2:             "bg-cyan-100 text-cyan-700",
  event:                "bg-purple-100 text-purple-700",
  sr_touch:             "bg-amber-100 text-amber-700",
  sr_breakout:          "bg-orange-100 text-orange-700",
  swing_breakout:       "bg-violet-100 text-violet-700",
  orb:                  "bg-lime-100 text-lime-700",
  compression_breakout: "bg-teal-100 text-teal-700",
  intraday_scalp:       "bg-rose-100 text-rose-700",
  tick:                 "bg-pink-100 text-pink-700",
  news:                 "bg-indigo-100 text-indigo-700",
};

const ALL_TRIGGERS = [
  "all", "timer", "timer_v2", "event", "sr_touch", "sr_breakout", "swing_breakout",
  "orb", "compression_breakout", "intraday_scalp", "tick", "news",
];

function scoreColor(score, isStock) {
  const alertThreshold = isStock ? ALERT_THRESHOLD_STOCK : ALERT_THRESHOLD_INDEX;
  const rawMin         = isStock ? RAW_LOG_MIN_STOCK     : RAW_LOG_MIN_INDEX;
  if (score >= alertThreshold) return "text-green-700 font-bold";
  if (score >= rawMin)         return "text-yellow-700 font-semibold";
  return "text-gray-400";
}

function scoreBg(score, isStock) {
  const alertThreshold = isStock ? ALERT_THRESHOLD_STOCK : ALERT_THRESHOLD_INDEX;
  const rawMin         = isStock ? RAW_LOG_MIN_STOCK     : RAW_LOG_MIN_INDEX;
  if (score >= alertThreshold) return "border-l-2 border-green-400";
  if (score >= rawMin)         return "border-l-2 border-yellow-400";
  return "border-l-2 border-gray-200";
}

function timeAgo(ts) {
  if (!ts) return "—";
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 5)  return "just now";
  if (diff < 60) return `${diff}s ago`;
  return `${Math.floor(diff / 60)}m ago`;
}
function toIST(ts) {
  if (!ts) return "\u2014";
  return new Date(ts * 1000).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    timeZone: "Asia/Kolkata", hour12: false,
  });
}
export default function PipelineScores({ scores = [] }) {
  const [triggerFilter, setTriggerFilter] = useState("all");
  const [dirFilter,     setDirFilter]     = useState("all");
  const [symbolSearch,  setSymbolSearch]  = useState("");
  const [sortBy,        setSortBy]        = useState("score");

  const filtered = useMemo(() => {
    let list = scores;
    if (triggerFilter !== "all") list = list.filter(s => s.trigger === triggerFilter);
    if (dirFilter     !== "all") list = list.filter(s => s.direction === dirFilter);
    if (symbolSearch.trim())     list = list.filter(s =>
      s.symbol?.toUpperCase().includes(symbolSearch.trim().toUpperCase())
    );
    if (sortBy === "score") return [...list].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    if (sortBy === "time")  return [...list].sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
    if (sortBy === "sym")   return [...list].sort((a, b) => (a.symbol ?? "").localeCompare(b.symbol ?? ""));
    return list;
  }, [scores, triggerFilter, dirFilter, symbolSearch, sortBy]);

  if (scores.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-gray-500 text-xs uppercase tracking-wider mb-2 font-semibold">Live Pipeline Scores</p>
        <p className="text-gray-400 text-xs py-3 text-center">No pipeline runs in the last 90 seconds</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold">Live Pipeline Scores</p>
          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
            {filtered.length}/{scores.length}
          </span>
        </div>
        <span className="text-[10px] text-gray-400">90s TTL — Redis only</span>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-2 mb-3">
        {/* Trigger filter */}
        <div className="flex flex-wrap gap-1">
          {ALL_TRIGGERS.map(t => (
            <button
              key={t}
              onClick={() => setTriggerFilter(t)}
              className={`text-[10px] px-2 py-0.5 rounded-full border font-medium transition-colors ${
                triggerFilter === t
                  ? "bg-gray-700 text-white border-gray-700"
                  : "bg-white text-gray-500 border-gray-300 hover:border-gray-500"
              }`}
            >
              {t === "all" ? "All" : (TRIGGER_LABELS[t] || t)}
            </button>
          ))}
        </div>

        {/* Direction filter */}
        <div className="flex gap-1">
          {["all", "BULLISH", "BEARISH"].map(d => (
            <button
              key={d}
              onClick={() => setDirFilter(d)}
              className={`text-[10px] px-2 py-0.5 rounded-full border font-medium transition-colors ${
                dirFilter === d
                  ? d === "BULLISH" ? "bg-green-600 text-white border-green-600"
                    : d === "BEARISH" ? "bg-red-600 text-white border-red-600"
                    : "bg-gray-700 text-white border-gray-700"
                  : "bg-white text-gray-500 border-gray-300 hover:border-gray-500"
              }`}
            >
              {d === "all" ? "Both" : d === "BULLISH" ? "▲ Bull" : "▼ Bear"}
            </button>
          ))}
        </div>

        {/* Symbol search */}
        <input
          type="text"
          value={symbolSearch}
          onChange={e => setSymbolSearch(e.target.value)}
          placeholder="Search symbol…"
          className="text-xs px-2 py-0.5 border border-gray-300 rounded focus:outline-none focus:border-blue-400 w-28 font-mono"
        />

        {/* Sort */}
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="text-xs px-2 py-0.5 border border-gray-300 rounded focus:outline-none focus:border-blue-400 bg-white text-gray-600"
        >
          <option value="score">Sort: Score</option>
          <option value="time">Sort: Time</option>
          <option value="sym">Sort: Symbol</option>
        </select>
      </div>

      {/* Score table */}
      {filtered.length === 0 ? (
        <p className="text-gray-400 text-xs py-2 text-center">No matches</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1 whitespace-nowrap">Symbol</th>
                <th className="text-left text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1 w-28">Score /12</th>
                <th className="text-right text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1 whitespace-nowrap">Pts</th>
                <th className="text-center text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1">Dir</th>
                <th className="text-left text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1 whitespace-nowrap">Trigger</th>
                <th className="text-left text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1 hidden sm:table-cell">Structure</th>
                <th className="text-right text-[10px] text-gray-400 font-semibold uppercase tracking-wide px-2 pb-1 whitespace-nowrap">IST</th>
              </tr>
            </thead>
            <tbody>
            {filtered.map((entry, i) => {
              const isStock  = entry.is_stock;
              const alertThr = isStock ? ALERT_THRESHOLD_STOCK : ALERT_THRESHOLD_INDEX;
              const rawMin   = isStock ? RAW_LOG_MIN_STOCK : RAW_LOG_MIN_INDEX;
              const score    = entry.score ?? 0;
              const pct      = Math.min(100, (score / 12) * 100);
              return (
                <tr key={`${entry.symbol}-${entry.ts}-${i}`} className="bg-gray-50 hover:bg-gray-100">
                  {/* Symbol */}
                  <td className={`px-2 py-1.5 font-mono font-semibold text-gray-800 whitespace-nowrap ${scoreBg(score, isStock)}`}>
                    {entry.symbol}
                  </td>

                  {/* Score bar — w-28 via th */}
                  <td className="px-2 py-1.5 w-28">
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full ${
                          score >= alertThr ? "bg-green-500" :
                          score >= rawMin   ? "bg-yellow-400" :
                          "bg-gray-300"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </td>

                  {/* Score value */}
                  <td className={`px-2 py-1.5 font-mono text-right whitespace-nowrap ${scoreColor(score, isStock)}`}>
                    {score.toFixed(1)}
                  </td>

                  {/* Direction */}
                  <td className={`px-2 py-1.5 text-[10px] text-center whitespace-nowrap ${entry.direction === "BULLISH" ? "text-green-600" : "text-red-500"}`}>
                    {entry.direction === "BULLISH" ? "▲" : "▼"}
                  </td>

                  {/* Trigger badge */}
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${TRIGGER_COLORS[entry.trigger] || "bg-gray-100 text-gray-500"}`}>
                      {TRIGGER_LABELS[entry.trigger] || entry.trigger}
                    </span>
                  </td>

                  {/* Structure — auto width, truncated */}
                  <td className="px-2 py-1.5 text-[9px] text-gray-400 hidden sm:table-cell max-w-[160px]">
                    <span className="block truncate" title={entry.structure}>
                      {entry.structure && entry.structure !== "NONE" ? entry.structure.replace(/_/g, " ") : "—"}
                    </span>
                  </td>

                  {/* IST time */}
                  <td className="px-2 py-1.5 text-[9px] text-gray-500 font-mono text-right whitespace-nowrap">
                    {toIST(entry.ts)}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 pt-2 border-t border-gray-100 text-[9px] text-gray-400">
        <span><span className="inline-block w-2 h-2 rounded-sm bg-green-400 mr-1" />≥ Alert threshold (Claude)</span>
        <span><span className="inline-block w-2 h-2 rounded-sm bg-yellow-400 mr-1" />≥ Raw-log floor (analysis only)</span>
        <span><span className="inline-block w-2 h-2 rounded-sm bg-gray-300 mr-1" />Below floor (no log)</span>
      </div>
    </div>
  );
}
